import UseJwtAuth from '@auth/services/jwt/useJwtAuth';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, CircularProgress } from '@mui/material';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';

/** Start of Initial Flow of Password Reset */
const initialSchema = z.object({
    email: z.string().email('You must enter a valid email').min(1, 'You must enter an email'),
});

type initialFormType = z.infer<typeof initialSchema>;

const initialDefaults = {
    email: '',
};

/**
 * Initial form component for password reset flow.
 * 
 * This component handles the first step of the password reset process:
 * - Displays a form to collect the user's email address
 * - Sends a password reset email with a link back to the application
 * - Shows success message after email is sent
 * - Auto-populates email field if provided in URL query parameters
 * 
 * @returns A form with email input and submit button, or success alert if email was sent
 */
function InitialForgotPasswordForm({
    text
}: Readonly<{
    text?: string
}>) {

    const [loading, setLoading] = useState<boolean>(false);
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const { forgotPassword } = UseJwtAuth();
    const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');

    const email = searchParams.get('email') || '';
    const key = searchParams.get('key') || '';

    // Create defaults with email if present, without mutating the module-level object
    const formDefaults = email ? { email } : initialDefaults;

    const initialForm = useForm<initialFormType>({
        resolver: zodResolver(initialSchema),
        defaultValues: formDefaults,
        mode: 'onChange'
    });

    useEffect(() => {
        if (Object.keys(initialForm.formState.dirtyFields).length && !Object.keys(initialForm.formState.touchedFields).length) {
            initialForm.trigger();
        }
    }, [initialForm, initialForm.formState.dirtyFields, initialForm.formState.touchedFields]);

    const onSubmit = async () => {
        setLoading(true);


        try {
            await forgotPassword({
                email: initialForm.getValues().email,
                app_url: window.location.origin + '/forgot-password'
            });
            setShowAlert(true);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            toast.error(err.data?.detail || 'An error occured while requesting Forgot Password, please contact support')
        }

        setLoading(false);
    };

    if (showAlert) {
        return <Alert severity="success" className="mt-12">
            If an account with that email exists, we've sent a password reset link. Please check your inbox and spam/junk folder.
        </Alert>;
    }

    if (!key)
        return <form
            id='forgot-password'
            noValidate
            className="mt-12 flex w-full flex-col justify-center space-y-10"
            onSubmit={initialForm.handleSubmit(onSubmit)}
        >
            <p className="text-lg">
                {text}
            </p>
            <Controller
                name="email"
                control={initialForm.control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        label="Email"
                        autoFocus
                        type="username"
                        error={!!initialForm.formState.errors.email}
                        helperText={initialForm.formState.errors?.email?.message}
                        variant="outlined"

                        required
                        fullWidth
                    />
                )}
            />

            <Button
                variant="contained"
                color="secondary"
                className=" w-full mt-24"
                aria-label="Sign in"
                disabled={isEmpty(
                    initialForm.formState.dirtyFields) || !initialForm.formState.isValid || loading}
                type="submit"
                size="large"
            >
                {loading && (
                    <CircularProgress
                        size={15}
                        className="mr-5"
                    />
                )}
                Send Reset Email
            </Button>

        </form>;
}

export default InitialForgotPasswordForm;